/**
 * Oddit global interactions — static preview bundle.
 * Adds html.oddit-js, stagger indices, reveal IO, section activation, parallax.
 * Reveals use threshold 0 so “peek-a-boo” carousel slides (only a sliver visible)
 * still get is-visible on load; a post-layout flush matches the same root bounds.
 *
 * Theme editor: re-applies visibility on shopify:section:load / editor load so
 * re-rendered sections are not stuck at opacity 0 (missing oddit-section-active / is-visible).
 */
(function () {
  'use strict';

  document.documentElement.classList.add('oddit-js');

  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  var staticPreview = document.documentElement.classList.contains(
    'oddit-static-preview',
  );

  var sectionShellObserver = null;
  var revealObserver = null;
  var parallaxInitialized = false;
  var requestParallaxUpdate = null;

  function isThemeEditor() {
    return (
      typeof window.Shopify !== 'undefined' &&
      window.Shopify.designMode === true
    );
  }

  function initStagger(root) {
    var scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll('[data-oddit-stagger]').forEach(function (container) {
      var idx = 0;
      Array.prototype.forEach.call(container.children, function (child) {
        if (child.classList && child.classList.contains('oddit-reveal')) {
          child.style.setProperty('--oddit-stagger-index', String(idx));
          idx += 1;
        }
      });
    });
  }

  /**
   * Mouse drag for horizontal tracks marked [data-oddit-drag-scroll].
   * Mouse only — touch keeps native scroll-snap and its momentum. The drag only
   * moves scrollLeft; snapping tracks settle to the nearest slide on release.
   */
  function initDragScroll(root) {
    var scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll('[data-oddit-drag-scroll]').forEach(function (track) {
      if (track._odditDragCtrl) {
        return;
      }
      var ctrl = new AbortController();
      var opts = { signal: ctrl.signal };
      var active = false;
      var moved = false;
      var startX = 0;
      var lastX = 0;

      // Read snapping off a CHILD's scroll-snap-align, not the track's
      // scroll-snap-type: `.is-dragging` suppresses the latter, so asking the
      // track mid-gesture always answers "no snap".
      function snaps() {
        var first = track.firstElementChild;
        return !!first && getComputedStyle(first).scrollSnapAlign !== 'none';
      }

      // ponytail: settles scroll-snap-align start/center, not end — no design uses end.
      function settle() {
        var style = getComputedStyle(track);
        var pad =
          parseFloat(style.scrollPaddingLeft) ||
          parseFloat(style.paddingInlineStart) ||
          0;
        var anchor = track.getBoundingClientRect().left + pad;
        var first = track.firstElementChild;
        if (first && getComputedStyle(first).scrollSnapAlign === 'center') {
          anchor +=
            (track.clientWidth - pad - first.getBoundingClientRect().width) / 2;
        }
        var nearest = null;
        Array.prototype.forEach.call(track.children, function (child) {
          var delta = child.getBoundingClientRect().left - anchor;
          if (nearest === null || Math.abs(delta) < Math.abs(nearest)) {
            nearest = delta;
          }
        });
        if (nearest) {
          track.scrollBy({ left: nearest, behavior: 'smooth' });
        }
      }

      function endSettle() {
        clearTimeout(track._odditSettleTimer);
        track._odditSettleTimer = null;
        track.classList.remove('is-settling');
      }

      // Snap stays suspended for the whole smooth settle. Re-enabling mandatory
      // snap first makes the engine yank the position to ITS nearest snap point
      // before our scroll runs, so the settle degrades to an instant jump.
      function endDrag() {
        if (!active) {
          return;
        }
        active = false;
        // Tracks without snap (tab strips) keep their free position.
        var willSettle = moved && snaps();
        // Add is-settling BEFORE dropping is-dragging so snap is never live for
        // even one style recalc between the two.
        if (willSettle) {
          track.classList.add('is-settling');
        }
        track.classList.remove('is-dragging');
        if (!willSettle) {
          return;
        }
        settle();
        if ('onscrollend' in window) {
          track.addEventListener('scrollend', endSettle, {
            once: true,
            signal: ctrl.signal,
          });
        }
        clearTimeout(track._odditSettleTimer);
        track._odditSettleTimer = setTimeout(endSettle, 600);
      }

      track.addEventListener(
        'pointerdown',
        function (e) {
          if (e.pointerType !== 'mouse' || e.button !== 0) {
            return;
          }
          active = true;
          moved = false;
          startX = e.clientX;
          lastX = e.clientX;
          track.classList.add('is-dragging');
        },
        opts,
      );

      window.addEventListener(
        'pointermove',
        function (e) {
          if (!active) {
            return;
          }
          track.scrollLeft -= e.clientX - lastX;
          lastX = e.clientX;
          if (Math.abs(e.clientX - startX) > 4) {
            moved = true;
          }
        },
        opts,
      );

      // Native image/link drag hijacks the pointer mid-gesture and the ghost
      // preview replaces the grab cursor, so the track never gets pointermove.
      track.addEventListener(
        'dragstart',
        function (e) {
          e.preventDefault();
        },
        opts,
      );

      window.addEventListener('pointerup', endDrag, opts);
      window.addEventListener('pointercancel', endDrag, opts);

      // Swallow the click that ends a drag so cards/tabs/videos don't activate.
      track.addEventListener(
        'click',
        function (e) {
          if (!moved) {
            return;
          }
          e.preventDefault();
          e.stopPropagation();
          moved = false;
        },
        { capture: true, signal: ctrl.signal },
      );

      // Wheel/trackpad scrolling slides every card under a stationary cursor,
      // so each one fires its hover lift/zoom in turn — a trail of half-played
      // transitions that reads as flicker. Hover is inert while the track moves.
      track.addEventListener(
        'scroll',
        function () {
          track.classList.add('is-scrolling');
          clearTimeout(track._odditScrollIdleTimer);
          track._odditScrollIdleTimer = setTimeout(function () {
            track._odditScrollIdleTimer = null;
            track.classList.remove('is-scrolling');
          }, 120);
        },
        { passive: true, signal: ctrl.signal },
      );

      track._odditDragCtrl = ctrl;
    });
  }

  function destroyDragScroll(root) {
    var scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll('[data-oddit-drag-scroll]').forEach(function (track) {
      if (!track._odditDragCtrl) {
        return;
      }
      track._odditDragCtrl.abort();
      track._odditDragCtrl = null;
      clearTimeout(track._odditSettleTimer);
      track._odditSettleTimer = null;
      clearTimeout(track._odditScrollIdleTimer);
      track._odditScrollIdleTimer = null;
      track.classList.remove('is-dragging');
      track.classList.remove('is-settling');
      track.classList.remove('is-scrolling');
    });
  }

  function ensureSectionShellObserver() {
    if (staticPreview || reducedMotion.matches) {
      return null;
    }
    if (!sectionShellObserver) {
      sectionShellObserver = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              entry.target.classList.add('oddit-section-active');
            }
          });
        },
        { rootMargin: '0px 0px -5% 0px', threshold: 0.01 },
      );
    }
    return sectionShellObserver;
  }

  function observeShopifySection(el) {
    if (staticPreview || reducedMotion.matches) {
      el.classList.add('oddit-section-active');
      return;
    }
    var io = ensureSectionShellObserver();
    if (io) {
      io.observe(el);
    }
  }

  /**
   * Axis-aligned intersection against the same effective root as the reveal
   * IntersectionObserver (viewport + rootMargin). Used to flush reveals after
   * layout so peek-a-boo slides are not stuck hidden for a frame.
   */
  function getRevealRootBounds() {
    var w = window.innerWidth || 0;
    var h = window.innerHeight || 0;
    var bottomInset = h * 0.08;
    return {
      top: 0,
      left: 0,
      right: w,
      bottom: h - bottomInset,
    };
  }

  function rectIntersectsRevealRoot(rect) {
    var root = getRevealRootBounds();
    return (
      rect.width > 0 &&
      rect.height > 0 &&
      rect.left < root.right &&
      rect.right > root.left &&
      rect.top < root.bottom &&
      rect.bottom > root.top
    );
  }

  /**
   * Mark any already-intersecting reveals visible (and unobserve). Covers
   * carousels that show part of the next slide: a small visible fraction can be
   * below the old 5% IO threshold; threshold 0 fixes IO, this catches late layout.
   */
  function flushInitiallyVisibleReveals(scope) {
    if (reducedMotion.matches || staticPreview || isThemeEditor()) {
      return;
    }
    var root = scope && scope.querySelectorAll ? scope : document;
    root.querySelectorAll('.oddit-reveal:not(.is-visible)').forEach(function (el) {
      if (rectIntersectsRevealRoot(el.getBoundingClientRect())) {
        el.classList.add('is-visible');
        if (revealObserver) {
          revealObserver.unobserve(el);
        }
      }
    });
  }

  function ensureRevealObserver() {
    if (reducedMotion.matches) {
      return null;
    }
    if (!revealObserver) {
      revealObserver = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              entry.target.classList.add('is-visible');
              revealObserver.unobserve(entry.target);
            }
          });
        },
        {
          root: null,
          rootMargin: '0px 0px -8% 0px',
          threshold: 0,
        },
      );
    }
    return revealObserver;
  }

  function observeReveal(el) {
    if (reducedMotion.matches) {
      el.classList.add('is-visible');
      return;
    }
    var io = ensureRevealObserver();
    if (io) {
      io.observe(el);
    }
  }

  function initShopifySections() {
    var sections = document.querySelectorAll('.shopify-section');
    if (!sections.length) {
      return;
    }

    if (staticPreview || reducedMotion.matches) {
      sections.forEach(function (el) {
        el.classList.add('oddit-section-active');
      });
      return;
    }

    var io = ensureSectionShellObserver();
    if (!io) {
      return;
    }
    sections.forEach(function (el) {
      io.observe(el);
    });
  }

  function initReveals() {
    var nodes = document.querySelectorAll('.oddit-reveal');
    if (!nodes.length) {
      return;
    }

    if (reducedMotion.matches) {
      nodes.forEach(function (el) {
        el.classList.add('is-visible');
      });
      return;
    }

    nodes.forEach(function (el) {
      observeReveal(el);
    });
  }

  function parallaxSpeed(el) {
    var raw = el.getAttribute('data-oddit-parallax-speed');
    var speed = raw != null ? parseFloat(raw) : 0.15;
    return Number.isNaN(speed) ? 0.15 : speed;
  }

  /**
   * The section the layer parallaxes against. Its box is the stable reference:
   * the layer's own getBoundingClientRect() would fold in the translate written
   * on the previous frame and feed back into the next one.
   */
  function parallaxHost(el) {
    return el.parentElement || el.offsetParent;
  }

  /**
   * A parallax layer has to be taller than its section, or translating it slides
   * the artwork out from under its own edges and exposes whatever sits behind.
   *
   * While any part of the host is on screen, `rect.top` spans `[-hostH, vh]`, so
   *
   *     |ty| <= 100 * speed * (hostH + vh) / (2 * vh)
   *
   * which is what `pad` covers. Note there is no viewport-*width* term: the travel
   * is the same at every breakpoint, so the buffer must be too.
   *
   * Written as inline style on purpose. A shared `.oddit-parallax { top: ... }` rule
   * is specificity (0,1,0) and would lose to the doubled-class selectors that the
   * cascade-defense convention emits for section CSS. Inline outranks all of them,
   * including any hand-rolled overscan a section still carries.
   */
  function ensureParallaxBuffer(el, speed, vh, hostH) {
    var key = vh + ':' + Math.round(hostH);
    if (el.getAttribute('data-oddit-parallax-fit') === key) {
      return;
    }
    el.setAttribute('data-oddit-parallax-fit', key);

    // top/bottom are inert on a static element and height:auto could collapse it.
    // A layer can be static at one breakpoint and absolute at another, so drop any
    // offsets written while it was positioned rather than leaving them stale.
    var position = window.getComputedStyle(el).position;
    if (position !== 'absolute' && position !== 'fixed') {
      el.style.top = '';
      el.style.bottom = '';
      el.style.height = '';
      return;
    }

    var pad = Math.ceil((100 * speed * (hostH + vh)) / (2 * vh)) + 2;
    el.style.top = -pad + 'px';
    el.style.bottom = -pad + 'px';
    el.style.height = 'auto';
  }

  function initParallax() {
    if (reducedMotion.matches) {
      return;
    }

    var ticking = false;

    function update() {
      ticking = false;
      var els = document.querySelectorAll('.oddit-parallax');
      var vh = window.innerHeight || 1;
      for (var i = 0; i < els.length; i++) {
        var el = els[i];
        var host = parallaxHost(el);
        if (!host) {
          continue;
        }
        var rect = host.getBoundingClientRect();
        var speed = parallaxSpeed(el);

        ensureParallaxBuffer(el, speed, vh, rect.height);

        if (rect.bottom < 0 || rect.top > vh) {
          continue;
        }

        var centerOffset = rect.top + rect.height / 2 - vh / 2;
        var relative = centerOffset / vh;
        var ty = relative * 100 * speed;
        el.style.transform = 'translate3d(0, ' + ty + 'px, 0)';
      }
    }

    function onFrame() {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(update);
      }
    }

    /* A width-only resize can cross a breakpoint that repositions the layer without
       changing the cache key, so invalidate every fit and let update() re-measure. */
    function onResize() {
      var els = document.querySelectorAll('.oddit-parallax');
      for (var i = 0; i < els.length; i++) {
        els[i].removeAttribute('data-oddit-parallax-fit');
      }
      onFrame();
    }

    requestParallaxUpdate = onFrame;

    if (!parallaxInitialized) {
      parallaxInitialized = true;
      window.addEventListener('scroll', onFrame, { passive: true });
      window.addEventListener('resize', onResize, { passive: true });
    }
    onFrame();
  }

  /**
   * Theme editor: new DOM has no oddit-section-active / is-visible; apply immediately.
   * Storefront section AJAX: wire IntersectionObserver for the replaced section subtree.
   */
  function attachInteractionsToSectionRoot(sectionRoot) {
    if (!sectionRoot || !sectionRoot.querySelectorAll) {
      return;
    }

    if (staticPreview || reducedMotion.matches) {
      initStagger(sectionRoot);
      if (sectionRoot.classList && sectionRoot.classList.contains('shopify-section')) {
        sectionRoot.classList.add('oddit-section-active');
      }
      sectionRoot.querySelectorAll('.oddit-reveal').forEach(function (el) {
        el.classList.add('is-visible');
      });
      return;
    }

    if (isThemeEditor()) {
      initStagger(sectionRoot);
      if (sectionRoot.classList && sectionRoot.classList.contains('shopify-section')) {
        sectionRoot.classList.add('oddit-section-active');
      }
      sectionRoot.querySelectorAll('.oddit-reveal').forEach(function (el) {
        el.classList.add('is-visible');
      });
      return;
    }

    initStagger(sectionRoot);
    if (sectionRoot.classList && sectionRoot.classList.contains('shopify-section')) {
      observeShopifySection(sectionRoot);
    }
    sectionRoot.querySelectorAll('.oddit-reveal').forEach(function (el) {
      observeReveal(el);
    });
    requestAnimationFrame(function () {
      flushInitiallyVisibleReveals(sectionRoot);
    });
  }

  function refreshAllThemeEditorInteractions() {
    if (!isThemeEditor()) {
      return;
    }
    initStagger();
    initDragScroll();
    document.querySelectorAll('.shopify-section').forEach(function (el) {
      el.classList.add('oddit-section-active');
    });
    document.querySelectorAll('.oddit-reveal').forEach(function (el) {
      el.classList.add('is-visible');
    });
  }

  function init() {
    initStagger();
    initDragScroll();
    initRebuyDragScroll();

    if (isThemeEditor()) {
      refreshAllThemeEditorInteractions();
      initParallax();
      return;
    }

    initShopifySections();
    initReveals();
    requestAnimationFrame(function () {
      flushInitiallyVisibleReveals(document);
    });
    initParallax();
  }

  function onShopifySectionLoad(e) {
    if (e && e.target) {
      attachInteractionsToSectionRoot(e.target);
      initDragScroll(e.target);
    }
    if (requestParallaxUpdate) {
      requestParallaxUpdate();
    } else {
      initParallax();
    }
  }

  document.addEventListener('shopify:section:load', onShopifySectionLoad);
  document.addEventListener('shopify:section:unload', function (e) {
    if (e && e.target) {
      destroyDragScroll(e.target);
    }
  });
  document.addEventListener('shopify:section:select', function (e) {
    if (e && e.target) {
      attachInteractionsToSectionRoot(e.target);
    }
    if (requestParallaxUpdate) {
      requestParallaxUpdate();
    }
  });
  document.addEventListener('shopify:block:select', function (e) {
    var section =
      e &&
      e.target &&
      e.target.closest &&
      e.target.closest('.shopify-section');
    if (section) {
      attachInteractionsToSectionRoot(section);
    }
    if (requestParallaxUpdate) {
      requestParallaxUpdate();
    }
  });
  document.addEventListener('shopify:editor:load', function () {
    refreshAllThemeEditorInteractions();
    if (requestParallaxUpdate) {
      requestParallaxUpdate();
    }
  });

  reducedMotion.addEventListener('change', function (e) {
    if (e.matches) {
      document.querySelectorAll('.shopify-section').forEach(function (el) {
        el.classList.add('oddit-section-active');
      });
      document.querySelectorAll('.oddit-reveal').forEach(function (el) {
        el.classList.add('is-visible');
      });
      if (sectionShellObserver) {
        sectionShellObserver.disconnect();
        sectionShellObserver = null;
      }
      if (revealObserver) {
        revealObserver.disconnect();
        revealObserver = null;
      }
    } else {
      if (staticPreview) {
        return;
      }
      if (isThemeEditor()) {
        refreshAllThemeEditorInteractions();
      } else {
        initShopifySections();
        document.querySelectorAll('.oddit-reveal:not(.is-visible)').forEach(function (el) {
          observeReveal(el);
        });
      }
      initParallax();
    }
  });

  /* App-injected markup (Rebuy widgets) lands after init and never fires
     shopify:section:load, so sections need a way to wire a late track. */
  window.odditInteractions = window.odditInteractions || {};
  window.odditInteractions.initDragScroll = initDragScroll;

  /* The Rebuy Smart Cart mounts after init and re-renders on every cart change,
     so its upsell strip has to be tagged for drag-scroll whenever it reappears.
     initDragScroll is idempotent per track (it stores its AbortController on
     the node), so re-running on re-render is safe. */
  function initRebuyDragScroll() {
    var pending = false;

    function tagTracks() {
      pending = false;
      var cart = document.getElementById('rebuy-cart');
      if (!cart) {
        return;
      }
      var tracks = cart.querySelectorAll(
        '.splide__track:not([data-oddit-drag-scroll])',
      );
      if (!tracks.length) {
        return;
      }
      tracks.forEach(function (track) {
        track.setAttribute('data-oddit-drag-scroll', '');
      });
      initDragScroll(cart);
      suppressCarouselTouch(cart);
    }

    /* The cart's carousel keeps its own touch drag, which preventDefaults the
       gesture to move a transform the cart stylesheet pins to none — a finger
       swipe then looks frozen. Capture the gesture ABOVE the track (listeners
       on the track itself fire in registration order, and the carousel bound
       first), so its handler never runs and native panning takes over. Mouse
       pointers pass through untouched for initDragScroll. */
    function suppressCarouselTouch(cart) {
      if (cart._odditCarouselTouchBound) {
        return;
      }
      cart._odditCarouselTouchBound = true;
      ['touchstart', 'pointerdown'].forEach(function (type) {
        cart.addEventListener(
          type,
          function (e) {
            if (e.type === 'pointerdown' && e.pointerType === 'mouse') {
              return;
            }
            if (e.target.closest && e.target.closest('.splide__track')) {
              e.stopPropagation();
            }
          },
          { capture: true },
        );
      });
    }

    new MutationObserver(function () {
      if (pending) {
        return;
      }
      pending = true;
      // setTimeout, not rAF: a background tab suspends animation frames, and
      // the cart can mount there (cart page open in another tab, prefetch).
      setTimeout(tagTracks, 0);
    }).observe(document.body, { childList: true, subtree: true });

    tagTracks();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
